import React from "react";
import Alert from "@material-ui/lab/Alert";
import CommuteIcon from "@material-ui/icons/Commute";
import { useHistory } from "react-router-dom";

import { LocalizedText } from "../LocalizedText";
import { Item, ItemList } from "../ItemList";
import { AddButton, DeleteButton, Panel, PanelHeader } from "../Panel";
import { useTranslation } from "../../hooks/i18n";
import { VehicleAccess } from "../../interfaces";
import {
  VehicleAccessListAdminPanelViewProps,
  VehicleAccessListViewProps,
} from "./interfaces";
import { useStyles, useVehicleAccessRowStyles } from "./styles";

export const VehicleAccessListAdminPanelView: React.FC<VehicleAccessListAdminPanelViewProps> = ({
  data,
  loading,
  error,
  accountId,
  onDelete,
  deleteSuccess,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [checkedItems, setCheckedItems] = React.useState<Item[]>([]);

  /**
   * Navigate to specific item when selected
   */
  const handleItemClick = (item: Item) => {
    history.push(
      `/accounts/summary/${accountId}/vehicle-access/edit/${item.id}`
    );
  };

  /**
   * Add or remove an item from state when checked/unchecked
   */
  const handleItemCheck = (item: Item, checked: boolean) => {
    setCheckedItems((checkedItems) => {
      const newCheckedItems = [...checkedItems];
      if (checked) {
        newCheckedItems.push(item);
      } else {
        newCheckedItems.splice(checkedItems.indexOf(item), 1);
      }
      return newCheckedItems;
    });
  };

  /**
   * Handle delete button click
   */
  const handleDelete = async () => {
    if (!window.confirm(t("confirm_delete_items"))) return;
    onDelete?.(checkedItems);
  };

  /**
   * Navigate to create vehicle when create button pressed
   */
  const handleCreate = () => {
    history.push(`/accounts/summary/${accountId}/vehicle-access/create`);
  };

  const header = (
    <PanelHeader icon={<CommuteIcon />} title={t("vehicle_access")}>
      {Boolean(checkedItems.length) && <DeleteButton onClick={handleDelete} />}

      <AddButton label="create_vehicle_access" onClick={handleCreate} />
    </PanelHeader>
  );

  return (
    <Panel header={header}>
      {deleteSuccess && (
        <Alert severity="info">
          <LocalizedText text="delete_success" />
        </Alert>
      )}

      <VehicleAccessListView
        data={data}
        loading={loading}
        error={error}
        onItemClick={handleItemClick}
        onItemCheck={handleItemCheck}
        checkboxes
      />
    </Panel>
  );
};

/**
 * A paged, searchable vehicle list view, optionally with checkboxes
 */
export const VehicleAccessListView: React.FC<VehicleAccessListViewProps> = ({
  data,
  loading,
  error,
  onItemClick,
  onItemCheck,
  checkboxes,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <div
      className={classes.container}
      aria-label={loading ? t("loading") : undefined}
    >
      {!loading && !error && (!data || data?.length === 0) && (
        <div className={classes.noResults}>
          <Alert severity="info">
            <LocalizedText text="no_vehicle_access" />
          </Alert>
        </div>
      )}

      {error && (
        <Alert severity="error">
          <LocalizedText text="an_error_occurred" />
        </Alert>
      )}

      {!error && (
        <ItemList
          items={((data as unknown) as Item[]) || []}
          numItems={25}
          loading={loading}
          onItemClick={onItemClick}
          onItemCheck={onItemCheck}
          checkboxes={checkboxes}
          renderItem={(item) => (
            <VehicleAccessRow
              vehicleAccess={(item as unknown) as VehicleAccess}
            />
          )}
        />
      )}
    </div>
  );
};

const VehicleAccessRow = ({
  vehicleAccess,
}: {
  vehicleAccess: VehicleAccess;
}) => {
  const classes = useVehicleAccessRowStyles();

  return (
    <div className={classes.container} data-testid="vehicle-access-row">
      <span>{vehicleAccess.name}</span>
    </div>
  );
};
